import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import Header from "@/components/Header";
import { formatPrice } from "@/components/ProductCard";
import { useAuth } from "@/context/AuthContext";
import { useFavorites } from "@/context/FavoritesContext";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { Heart, Trash, ShoppingCart } from "@phosphor-icons/react";

const PLACEHOLDER = "https://images.unsplash.com/photo-1495100497150-fe209c585f50?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";

export default function Favorites() {
  const { user, login } = useAuth();
  const { ids, toggle } = useFavorites();
  const { add } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    try { const res = await api.get("/favorites"); setProducts(res.data.products); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user, ids.length]);

  if (!user) {
    return (
      <div className="App min-h-screen bg-background">
        <Header />
        <div className="max-w-md mx-auto px-4 py-20 text-center">
          <div className="bg-white rounded-lg nb-shadow p-8">
            <Heart weight="bold" size={40} className="mx-auto text-red-500" />
            <h1 className="text-2xl font-extrabold tracking-tight mt-4">Mis favoritos</h1>
            <p className="text-muted-foreground mt-2">Inicia sesión para ver tus productos guardados.</p>
            <button onClick={login} className="mt-6 w-full nb-btn bg-primary text-primary-foreground py-3 hover:brightness-110">Ingresar</button>
          </div>
        </div>
      </div>
    );
  }

  const addToCart = (p) => {
    if (p.variants && p.variants.length) { toast("Elige una variante en el producto"); return; }
    add(p.id, null, 1); toast.success("Agregado al carrito");
  };

  return (
    <div className="App min-h-screen bg-background">
      <Header />
      <div className="max-w-[1200px] mx-auto px-3 sm:px-5 py-6">
        <h1 className="text-2xl font-extrabold tracking-tight flex items-center gap-2 mb-5"><Heart weight="fill" size={26} className="text-red-500" /> Mis favoritos</h1>
        {loading ? (
          <p className="text-muted-foreground">Cargando…</p>
        ) : products.length === 0 ? (
          <div className="bg-white rounded-lg nb-shadow p-10 text-center" data-testid="empty-favorites">
            <p className="text-lg font-bold">Aún no tienes favoritos</p>
            <p className="text-muted-foreground mt-1">Toca el corazón en cualquier producto para guardarlo.</p>
            <Link to="/" className="mt-5 inline-block nb-btn bg-primary text-primary-foreground px-6 py-3 hover:brightness-110">Explorar productos</Link>
          </div>
        ) : (
          <div className="bg-white rounded-lg nb-shadow divide-y divide-black/5" data-testid="favorites-list">
            {products.map((p) => (
              <div key={p.id} className="p-4 flex gap-4 items-center" data-testid={`favorite-${p.id}`}>
                <Link to={`/producto/${p.id}`} className="w-20 h-20 rounded-md overflow-hidden bg-secondary shrink-0">
                  <img src={p.main_image ? fileUrl(p.main_image) : PLACEHOLDER} alt="" className="w-full h-full object-cover" onError={(e)=>{e.currentTarget.src=PLACEHOLDER;}} />
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/producto/${p.id}`} className="font-medium hover:text-primary line-clamp-2">{p.title}</Link>
                  <p className="font-bold mt-1">{formatPrice(p.price)}</p>
                  {(p.in_stock === false) && <p className="text-xs text-destructive">Sin stock</p>}
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button onClick={() => addToCart(p)} data-testid={`fav-add-cart-${p.id}`} className="nb-btn bg-primary/10 text-primary px-3 py-2 text-sm flex items-center gap-1 hover:bg-primary/20"><ShoppingCart weight="bold" size={15} /> Agregar</button>
                  <Link to={`/producto/${p.id}`} className="nb-btn bg-primary text-primary-foreground px-3 py-2 text-sm text-center hover:brightness-110">Comprar</Link>
                  <button onClick={() => toggle(p.id)} data-testid={`fav-remove-${p.id}`} className="nb-btn bg-secondary px-3 py-2 text-sm hover:bg-destructive hover:text-white"><Trash weight="bold" size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
